import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { Button, Box, Typography, Alert, Snackbar, Autocomplete, TextField, FormControlLabel, Switch, Link, Chip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { Link as RouterLink, useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { DataTable } from '../components/DataTable';
import { DeviceForm } from '../components/devices/DeviceForm';
import { devicesApi, locationsApi } from '../api/services';
import { usePaginatedQuery } from '../hooks/usePaginatedQuery';
import { extractApiError } from '../utils/apiErrors';
import type { Device } from '../api/config';

export const DevicesPage: React.FC = () => {
  const [isFormOpen, setIsFormOpen] = React.useState(false);
  const [selectedDevice, setSelectedDevice] = React.useState<Device | undefined>();
  const [snackbar, setSnackbar] = React.useState<{ message: string; severity: 'error' | 'success' } | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedLocationId = searchParams.get('location_id');
  const location = useLocation();
  const navigate = useNavigate();

  // A page that redirects here (DeviceDetailPage after a delete) hands its
  // result over as navigation state; show it once and clear it so a refresh
  // doesn't repeat it.
  React.useEffect(() => {
    const handedOver = (location.state as { snackbar?: { message: string; severity: 'error' | 'success' } } | null)?.snackbar;
    if (handedOver) {
      setSnackbar(handedOver);
      navigate(location.pathname + location.search, { replace: true, state: null });
    }
  }, [location.state, location.pathname, location.search, navigate]);
  const [showUnassigned, setShowUnassigned] = React.useState(false);
  const [showStagingOnly, setShowStagingOnly] = React.useState(false);

  const queryClient = useQueryClient();

  const filters = React.useMemo(() => ({
    location_id: selectedLocationId ? Number(selectedLocationId) : undefined,
    unassigned: showUnassigned || undefined,
    staging: showStagingOnly ? true : undefined,
  }), [selectedLocationId, showUnassigned, showStagingOnly]);

  const {
    rows: devices,
    rowCount,
    isLoading,
    error,
    paginationModel,
    onPaginationModelChange,
    sortModel,
    onSortModelChange,
    filterModel,
    onFilterModelChange,
  } = usePaginatedQuery({
    queryKey: ['devices', selectedLocationId, showUnassigned, showStagingOnly],
    queryFn: (f, p) => devicesApi.getPage(f, p),
    filters,
    defaultSort: { field: 'created_at', sort: 'desc' },
  });

  const { data: locations = [] } = useQuery({
    queryKey: ['locations'],
    queryFn: () => locationsApi.getAll(),
  });

  const createMutation = useMutation({
    mutationFn: devicesApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devices'] });
      queryClient.invalidateQueries({ queryKey: ['locations'] });
      setSnackbar({ message: 'Device created successfully', severity: 'success' });
      handleCloseForm();
    },
    onError: (error) => {
      setSnackbar({ message: extractApiError(error, 'Failed to create device'), severity: 'error' });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<Device> }) =>
      devicesApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devices'] });
      queryClient.invalidateQueries({ queryKey: ['locations'] });
      setSnackbar({ message: 'Device updated successfully', severity: 'success' });
      handleCloseForm();
    },
    onError: (error) => {
      setSnackbar({ message: extractApiError(error, 'Failed to update device'), severity: 'error' });
    },
  });

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 60 },
    {
      field: 'device_id',
      headerName: 'Device ID',
      width: 260,
      renderCell: (params: GridRenderCellParams<Device>) => (
        <Link component={RouterLink} to={`/devices/${params.row.id}`} underline="hover">
          {params.row.device_id}
        </Link>
      ),
    },
    {
      field: 'location',
      headerName: 'Location',
      width: 250,
      valueGetter: (params: GridRenderCellParams<Device>) => {
        return params.row?.location?.name || 'Unassigned';
      },
    },
    { field: 'label', headerName: 'Label', width: 120 },
    {
      field: 'staging',
      headerName: 'Staging',
      width: 110,
      type: 'boolean',
      renderCell: (params: GridRenderCellParams<Device>) =>
        params.row?.staging
          ? <Chip size="small" label="Staging" color="warning" variant="outlined" />
          : null,
    },
    {
      field: 'power_configuration',
      headerName: 'Power',
      width: 130,
      valueGetter: (params: GridRenderCellParams<Device>) => {
        return params.row?.power_configuration || 'Unknown';
      },
    },
    {
      field: 'created_at',
      headerName: 'Created',
      filterable: false,
      width: 180,
      valueGetter: (params: GridRenderCellParams<Device>) => {
        const date = params.row?.created_at;
        return date ? new Date(date).toLocaleDateString() : '';
      },
    },
  ];

  const handleCloseForm = () => {
    setIsFormOpen(false);
    createMutation.reset();
    updateMutation.reset();
  };

  const mutationError = createMutation.error || updateMutation.error;

  const handleSubmit = (data: Partial<Device>) => {
    createMutation.reset();
    updateMutation.reset();
    if (selectedDevice) {
      updateMutation.mutate({ id: selectedDevice.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleEdit = (id: string) => {
    const device = devices.find((d) => d.id.toString() === id);
    if (device) {
      setSelectedDevice(device);
      setIsFormOpen(true);
    }
  };

  const handleLocationFilterChange = (_event: React.SyntheticEvent, loc: { id: number; name: string } | null) => {
    if (loc) {
      setSearchParams({ location_id: loc.id.toString() });
      setShowUnassigned(false);
    } else {
      setSearchParams({});
    }
  };

  if (error) {
    return <Alert severity="error">Error loading devices</Alert>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5" component="h1">
          Devices
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setIsFormOpen(true)}
        >
          Add Device
        </Button>
      </Box>

      <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
        <Autocomplete
          options={locations}
          value={locations.find(loc => loc.id.toString() === selectedLocationId) ?? null}
          onChange={handleLocationFilterChange}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderInput={(params) => (
            <TextField {...params} label="Filter by Location" />
          )}
          sx={{ minWidth: 250 }}
        />
        <FormControlLabel
          control={
            <Switch
              checked={showUnassigned}
              onChange={(e) => {
                setShowUnassigned(e.target.checked);
                if (e.target.checked) setSearchParams({});
              }}
            />
          }
          label="Unassigned only"
        />
        <FormControlLabel
          control={
            <Switch
              checked={showStagingOnly}
              onChange={(e) => setShowStagingOnly(e.target.checked)}
            />
          }
          label="Staging only"
        />
      </Box>

      <DataTable
        rows={devices}
        columns={columns}
        loading={isLoading}
        onEditClick={handleEdit}
        getRowId={(row: Device) => row.id.toString()}
        fullHeight
        paginationMode="server"
        sortingMode="server"
        rowCount={rowCount}
        paginationModel={paginationModel}
        onPaginationModelChange={onPaginationModelChange}
        sortModel={sortModel}
        onSortModelChange={onSortModelChange}
        filterModel={filterModel}
        onFilterModelChange={onFilterModelChange}
        pageSizeOptions={[25, 50, 100]}
      />

      <DeviceForm
        open={isFormOpen}
        onClose={handleCloseForm}
        onSubmit={handleSubmit}
        initialData={selectedDevice}
        prefilledLocationId={selectedLocationId ? Number(selectedLocationId) : undefined}
        onExited={() => setSelectedDevice(undefined)}
        error={mutationError ? extractApiError(mutationError) : undefined}
        isPending={createMutation.isPending || updateMutation.isPending}
      />

      <Snackbar
        open={!!snackbar}
        autoHideDuration={6000}
        onClose={() => setSnackbar(null)}
      >
        <Box>
          {snackbar && (
            <Alert
              severity={snackbar.severity}
              onClose={() => setSnackbar(null)}
              sx={{ width: '100%' }}
            >
              {snackbar.message}
            </Alert>
          )}
        </Box>
      </Snackbar>
    </Box>
  );
};
